// frontend/src/pages/Security.js
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../api';

export default function Security() {
  // ──────────────────────────────────────────────────────────────
  // States
  // ──────────────────────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(0);               // 0 = درخواست کد، 2 = تغییر رمز
  const [isCodeSent, setIsCodeSent] = useState(false); // برای Bottom Sheet
  const [code, setCode] = useState(['', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const codeInputRef = useRef([]);
  const isRequestSent = useRef(false);

  // ──────────────────────────────────────────────────────────────
  // تایمر
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let interval;
    if (isCodeSent && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer <= 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [isCodeSent, timer]);

  // ──────────────────────────────────────────────────────────────
  // بارگذاری کاربر
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }

    const fetchProfile = async () => {
      try {
        const res = await api.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (err) {
        setError('خطا در دریافت اطلاعات کاربر');
        setTimeout(() => setError(''), 3000);
        if (err.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          navigate('/login_page');
        }
      }
    };

    fetchProfile();
  }, [token, navigate]);

  // ──────────────────────────────────────────────────────────────
  // Web OTP – پر شدن خودکار کد
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isCodeSent) return;
    if (!('OTPCredential' in window)) return;

    const ac = new AbortController();

    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
        signal: ac.signal,
      })
      .then((otp) => {
        if (otp && otp.code && otp.code.length === 5) {
          const digits = otp.code.split('');
          setCode(digits);
          setTimeout(() => codeInputRef.current[4]?.focus(), 100);
          // تأیید خودکار (اختیاری)
          setTimeout(() => handleVerifyCode({ preventDefault: () => {} }), 600);
        }
      })
      .catch(() => {});

    return () => ac.abort();
  }, [isCodeSent]);

  // ──────────────────────────────────────────────────────────────
  // درخواست کد
  // ──────────────────────────────────────────────────────────────
  const handleRequestCode = async () => {
    if (isRequestSent.current) return;
    isRequestSent.current = true;

    try {
      await api.post(
        '/api/auth/request-verification-code',
        { phone_number: user.user.phone_number },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsCodeSent(true);
      setTimer(60);
      setCanResend(false);
      setSuccess('کد تأیید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال کد');
      setTimeout(() => setError(''), 3000);
    } finally {
      isRequestSent.current = false;
    }
  };

  // ──────────────────────────────────────────────────────────────
  // ارسال مجدد کد
  // ──────────────────────────────────────────────────────────────
  const handleResendCode = async () => {
    if (isRequestSent.current) return;
    isRequestSent.current = true;

    try {
      await api.post(
        '/api/auth/request-verification-code',
        { phone_number: user.user.phone_number },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCode(['', '', '', '', '']);
      setTimer(60);
      setCanResend(false);
      setSuccess('کد جدید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال مجدد');
      setTimeout(() => setError(''), 3000);
    } finally {
      isRequestSent.current = false;
    }
  };

  // ──────────────────────────────────────────────────────────────
  // تغییر کد (فیلدهای ۵ تایی)
  // ──────────────────────────────────────────────────────────────
  const handleCodeChange = (e, index) => {
    const value = e.target.value.slice(0, 1);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 4) {
      codeInputRef.current[index + 1]?.focus();
    } else if (!value && index > 0) {
      codeInputRef.current[index - 1]?.focus();
    }
  };

  // ──────────────────────────────────────────────────────────────
  // تأیید کد
  // ──────────────────────────────────────────────────────────────
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const verificationCode = code.join('');
    if (verificationCode.length !== 5) {
      setError('لطفاً کد کامل را وارد کنید');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      await api.post(
        '/api/auth/verify-profile-code',
        { phone_number: user.user.phone_number, code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsCodeSent(false);
      setStep(2);
      setSuccess('کد تأیید شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'کد اشتباه است');
      setTimeout(() => setError(''), 3000);
    }
  };

  // ──────────────────────────────────────────────────────────────
  // تغییر رمز عبور
  // ──────────────────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('رمز عبور و تأیید آن مطابقت ندارند');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (formData.password.length < 6) {
      setError('رمز عبور باید حداقل ۶ کاراکتر باشد');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      await api.put(
        '/api/users/change-password',
        { password: formData.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('رمز عبور با موفقیت تغییر کرد');
      setTimeout(() => navigate('/profile_page'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در تغییر رمز');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ──────────────────────────────────────────────────────────────
  // Toast
  // ──────────────────────────────────────────────────────────────
  const Toast = ({ type, message }) => (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 ${
        type === 'error'
          ? 'bg-red-500/90 text-white border-red-300'
          : 'bg-green-500/90 text-white border-green-300'
      } backdrop-blur-lg`}
    >
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        {type === 'error' ? (
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          />
        ) : (
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          />
        )}
      </svg>
      <span className="font-medium">{message}</span>
    </motion.div>
  );

  // ──────────────────────────────────────────────────────────────
  // Loading
  // ──────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
          <p className="text-gray-600 font-medium">در حال بارگذاری...</p>
        </motion.div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-md mx-auto">

          {/* پیام‌ها */}
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-sm px-4">
            <AnimatePresence>
              {error && <Toast type="error" message={error} />}
              {success && <Toast type="success" message={success} />}
            </AnimatePresence>
          </div>

          {/* دکمه برگشت */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -5 }}
            onClick={() => navigate('/profile_page')}
            className="flex items-center text-indigo-600 font-medium mb-6"
          >
            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            برگشت
          </motion.button>

          {/* مرحله 0 – درخواست کد */}
          {!isCodeSent && step === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8"
            >
              <h2 className="text-2xl font-bold text-center text-indigo-800 mb-6">تغییر رمز عبور</h2>
              <p className="text-center text-sm text-gray-600 mb-8">
                برای تغییر رمز عبور، ابتدا باید هویت خود را تأیید کنید.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleRequestCode}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                درخواست کد تأیید
              </motion.button>
            </motion.div>
          )}

          {/* مرحله 2 – تغییر رمز */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8"
            >
              <h2 className="text-2xl font-bold text-center text-indigo-800 mb-6">رمز عبور جدید</h2>

              <form onSubmit={handleChangePassword} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور جدید</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">تأیید رمز عبور</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50"
                    required
                  />
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  تغییر رمز عبور
                </motion.button>
              </form>
            </motion.div>
          )}
        </div>

        {/* ────────────────────── Bottom Sheet – تأیید کد (دقیقاً مثل Trade) ────────────────────── */}
        <AnimatePresence>
          {isCodeSent && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setIsCodeSent(false);
                  setCode(['', '', '', '', '']);
                  setTimer(60);
                  setCanResend(false);
                }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              />

              {/* Sheet */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="fixed left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 overflow-y-auto pb-8"
                style={{ bottom: 0, maxHeight: '90vh' }}
              >
                <div className="p-6 pb-safe">
                  {/* Drag handle */}
                  <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />

                  <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                    تأیید هویت
                  </h3>

                  {/* کادر خلاصه اطلاعات */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-200">
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">نوع:</span>
                        <span className="font-medium">تغییر رمز عبور</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">شماره:</span>
                        <span className="font-mono font-bold">{user.user.phone_number}</span>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleVerifyCode} className="space-y-6">
                    <p className="text-center text-lg font-medium text-gray-700">کد تأیید را وارد کنید</p>

                    {/* 5-digit inputs */}
                    <div className="flex justify-center gap-3" dir="ltr">
                      {code.map((digit, i) => (
                        <input
                          key={i}
                          id={`code-${i}`}
                          ref={(el) => (codeInputRef.current[i] = el)}
                          type="tel"
                          inputMode="numeric"
                          value={digit}
                          onChange={(e) => handleCodeChange(e, i)}
                          className="w-16 h-16 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all shadow-sm"
                          maxLength={1}
                          required
                        />
                      ))}
                    </div>

                    {/* Resend */}
                    <div className="text-center">
                      {!canResend ? (
                        <p className="text-sm text-gray-500">{timer} ثانیه تا ارسال مجدد</p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendCode}
                          className="text-indigo-600 hover:text-indigo-700 font-bold"
                        >
                          ارسال مجدد کد
                        </button>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 mt-6">
                      <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
                      >
                        تأیید و ادامه
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsCodeSent(false);
                          setCode(['', '', '', '', '']);
                          setTimer(60);
                          setCanResend(false);
                        }}
                        className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-red-600 hover:to-rose-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
                      >
                        انصراف
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}